// ============================================================================
// UNIVERSAL THEME DEFINITIONS -------------------------------------------------
// This single file merges **every** property that existed in your two former
// theme files. Absolutely nothing has been deleted-only combined. When either
// of the originals had something the other didn’t, it was added here.          
// ============================================================================
// -----------------------------------------------------------------------------
// Color system ----------------------------------------------------------------
// -----------------------------------------------------------------------------

export interface ThemeColors {
  primary: string;
  secondary: string;
  accent1: string;
  accent2: string;
  neutral1: string;
  neutral2: string;
  success: string;
  danger: string;
  warning: string;
  info: string;
  light: string;
  dark: string;
  white: string;
  background: {
    primary: string;
    secondary: string;
    light: string;
  };
  text: {
    primary: string;
    secondary: string;
    light: string;
    dark: string;
    muted: string;
    // Accessible opacity variants
    dark80: string;
    dark99: string;
    primary80: string;
  };
  border: {
    light: string;
    medium: string;
    dark: string;
  };
}

// -----------------------------------------------------------------------------
// Typography -------------------------------------------------------------------
// -----------------------------------------------------------------------------
export interface ThemeFonts {
  body: string;
  heading: string;
  mono: string;
}

export interface ThemeFontSizes {
  xs: string;
  sm: string;
  md: string;
  lg: string;
  xl: string;
  '2xl': string;
  '3xl': string;
  '4xl': string;
  '5xl': string;
}

export interface ThemeFontWeights {
  normal: number;
  medium: number;
  semibold: number;
  bold: number;
  extrabold: number;
}

export interface ThemeTypography {
  fontFamily: string;
  fontSizes: {
    xs: string;
    sm: string;
    md: string;
    lg: string;
    xl: string;
    xxl: string;
  };
  fontWeights: {
    light: number;
    regular: number;
    medium: number;
    semibold: number;
    bold: number;
  };
  lineHeights: {
    tight: number;
    normal: number;
    loose: number;
  };
}

// -----------------------------------------------------------------------------
// Layout & Sizing --------------------------------------------------------------
// -----------------------------------------------------------------------------
export interface ThemeSpacing {
  '0': string;
  '1': string;
  '2': string;
  '3': string;
  '4': string;
  '5': string;
  '6': string;
  '8': string;
  '10': string;
  '12': string;
  '16': string;
  '20': string;
  xs: string;
  sm: string;
  md: string;
  lg: string;
  xl: string;
  xxl: string;
}

export interface ThemeBreakpoints {
  xs: string;
  sm: string;
  md: string;
  lg: string;
  xl: string;
  '2xl': string;
}

export interface ThemeBorderRadius {
  none: string;
  sm: string;
  md: string;
  lg: string;
  xl: string;
  '2xl': string;
  '3xl': string;
  full: string;
  round?: string; // kept from the 2nd theme
}

// -----------------------------------------------------------------------------
// Visual Extras ----------------------------------------------------------------
// -----------------------------------------------------------------------------
export interface ThemeShadows {
  sm: string;
  md: string;
  lg: string;
  xl?: string;
  '2xl'?: string;
}

export interface ThemeTransitions {
  default: string;
  slow: string;
  fast: string;
}

export interface ThemeMobile {
  minTouchTarget: string;
  safeTouchTarget: string;
  maxContentWidth: string;
  headerHeight: string;
  bottomNavHeight: string;
  sideMargin: string;
}

export interface ThemeZIndex {
  // generic layers (from the 2nd theme)
  low?: number;
  medium?: number;
  high?: number;
  // detailed layers (from the 1st theme)
  dropdown?: number;
  sticky?: number;
  fixed?: number;
  modalBackdrop?: number;
  modal?: number;
  popover?: number;
  tooltip: number;
  toast?: number;
}

// -----------------------------------------------------------------------------
// Master Theme Interface -------------------------------------------------------
// -----------------------------------------------------------------------------
export interface Theme {
  colors: ThemeColors;
  fonts: ThemeFonts;
  fontSizes: ThemeFontSizes;
  fontWeights: ThemeFontWeights;
  typography: ThemeTypography;
  spacing: ThemeSpacing;
  breakpoints: ThemeBreakpoints;
  shadows: ThemeShadows;
  borderRadius: ThemeBorderRadius;
  transitions: ThemeTransitions;
  mobile: ThemeMobile;
  zIndex: ThemeZIndex;
}

// -----------------------------------------------------------------------------
// Master Theme Object ----------------------------------------------------------
// -----------------------------------------------------------------------------
// Color values reference CSS variables defined in src/styles/theme.css so that
// JS consumers automatically respond to dark mode. To change a color, edit
// theme.css - never replace the var() refs below with literals.
export const theme: Theme = {
  colors: {
    primary: 'var(--color-primary)',
    secondary: 'var(--brand-secondary)',
    accent1: 'var(--brand-accent-green)',
    accent2: 'var(--brand-accent-pink)',
    neutral1: 'var(--brand-cream)',
    neutral2: 'var(--brand-primary-light)',
    success: 'var(--color-success)',
    danger: 'var(--color-danger)',
    warning: 'var(--color-warning)',
    info: 'var(--color-info)',
    light: 'var(--color-bg-subtle)',
    dark: 'var(--color-surface-inverse)',
    white: '#ffffff',
    background: {
      primary: 'var(--color-surface)',
      secondary: 'var(--color-bg-subtle)',
      light: 'var(--color-bg-muted)',
    },
    text: {
      primary: 'var(--color-text)',
      secondary: 'var(--color-text-secondary)',
      light: 'var(--color-text-inverse)',
      dark: 'var(--color-text)',
      muted: 'var(--color-text-muted)',
      // Opacity variants kept for back-compat; map to mode-aware tokens.
      dark80: 'var(--color-text)',
      dark99: 'var(--color-text)',
      primary80: 'var(--color-primary)',
    },
    border: {
      light: 'var(--color-border-subtle)',
      medium: 'var(--color-border)',
      dark: 'var(--color-border-strong)',
    },
  },

  fonts: {
    body: "system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, 'Noto Sans', sans-serif",
    heading: "system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, 'Noto Sans', sans-serif",
    mono: "'Source Code Pro', monospace",
  },

  fontSizes: {
    xs: '0.75rem',
    sm: '0.875rem',
    md: '1rem',
    lg: '1.125rem',
    xl: '1.25rem',
    '2xl': '1.5rem',
    '3xl': '1.875rem',
    '4xl': '2.25rem',
    '5xl': '3rem',
  },

  fontWeights: {
    normal: 400,
    medium: 500,
    semibold: 600,
    bold: 700,
    extrabold: 800,
  },

  typography: {
    fontFamily: "system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, 'Noto Sans', sans-serif",
    fontSizes: {
      xs: '0.75rem',
      sm: '0.875rem',
      md: '1rem',
      lg: '1.25rem',
      xl: '1.5rem',
      xxl: '2rem',
    },
    fontWeights: {
      light: 300,
      regular: 400,
      medium: 500,
      semibold: 600,
      bold: 700,
    },
    lineHeights: {
      tight: 1.25,
      normal: 1.5,
      loose: 1.75,
    },
  },

  spacing: {
    '0': '0',
    '1': '0.25rem',
    '2': '0.5rem',
    '3': '0.75rem',
    '4': '1rem',
    '5': '1.25rem',
    '6': '1.5rem',
    '8': '2rem',
    '10': '2.5rem',
    '12': '3rem',
    '16': '4rem',
    '20': '5rem',
    xs: '4px',
    sm: '8px',
    md: '16px',
    lg: '24px',
    xl: '32px',
    xxl: '48px',
  },

  breakpoints: {
    xs: '480px',
    sm: '640px',
    md: '768px',
    lg: '1024px',
    xl: '1280px',
    '2xl': '1536px',
  },

  shadows: {
    sm: '0 1px 2px 0 rgba(0, 0, 0, 0.05)',
    md: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
    lg: '0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)',
    xl: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)',
    '2xl': '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
  },

  borderRadius: {
    none: '0',
    sm: '0.125rem',
    md: '0.375rem',
    lg: '0.5rem',
    xl: '0.75rem',
    '2xl': '1rem',
    '3xl': '1.5rem',
    full: '9999px',
    round: '50%',
  },

  transitions: {
    default: 'all 0.3s ease',
    slow: 'all 0.5s ease',
    fast: 'all 0.2s ease',
  },

  mobile: {
    minTouchTarget: '44px',
    safeTouchTarget: '48px',
    maxContentWidth: '100vw',
    headerHeight: '60px',
    bottomNavHeight: '60px',
    sideMargin: '16px',
  },

  zIndex: {
    low: 10,
    medium: 20,
    high: 30,
    dropdown: 1000,
    sticky: 1020,
    fixed: 1030,
    modalBackdrop: 1040,
    modal: 1050,
    popover: 1060,
    tooltip: 1100,
    toast: 1080,
  },
} as const; // <- keeps literal types without mutating
